import "dayjs/locale/vi"

import { useMemo, useState } from "react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { toast } from "react-toastify"
import { appStakeUnstakePopupState, appStopUnstakingPopupState } from "@/atoms/appPopupState"
import { restakingAccountAtom } from "@/atoms/restakingState"
import { networkAtom, refreshRestakingAtom } from "@/atoms/settings"
import { RelendTooltip } from "@/components/relendTooltip"
import { DEFAULT_DECIMALS, STAKING, UNSTAKING, WITHDRAW_UNSTAKING } from "@/utils/constants"
import { RestakingAccountState, TransactionDetail } from "@/utils/dataTypes"
import { stringifyVariable } from "@/utils/helpers"
import { formatToken } from "@/utils/numberFormatter"
import { Box, Link, List, ListItem, Stack, Typography } from "@mui/material"
import { useRollbar } from "@rollbar/react"
import { DEFAULT_TRANSACTION_DETAIL, handleRestakingError, handleRestakingSuccessful } from "../../shared/commons"
import { StakeUnstakeForm } from "../stakeUnstakeForm"
import { RelendModal } from "@/components/relendModal"
import { ModalTxResult } from "@/components/modalTxResult"
import InfoIcon from "@/assets/icons/base64/InfoIcon"
import Image from "next/image"
import Button from "@/components/base/Button"
import useRestaking from "@/hooks/useRestaking"
import useTrans from "@/hooks/useTrans"
import ItemInfo from "../../shared/ItemInfo"

import { listItem } from "./styles"
/**
 * The function `getButtonUnstakeDisplay` determines the display text for a button based on the state
 * of a restaking account and the time remaining till unlock.
 */
const getButtonTextUnstakeDisplay = (
  restakingAccount: RestakingAccountState | null,
  timeRemainingTillUnlock: string | null,
  trans: any,
) => {
  if (restakingAccount) {
    const unstakedAmount = restakingAccount.unstakedAmount.toNumber()
    if (unstakedAmount > 0) {
      if (timeRemainingTillUnlock) {
        return trans.staking.detail.stop_unstake
      } else {
        return trans.staking.detail.withdraw
      }
    } else {
      return trans.staking.detail.start_unstake
    }
  } else {
    return trans.staking.detail.start_unstake
  }
}

type UnstakeProps = {
  handleOpenStakingModal: (type: typeof STAKING | typeof UNSTAKING) => void
}

const Unstake = ({ handleOpenStakingModal }: UnstakeProps) => {
  const trans = useTrans()
  const { address, stakedAmountUI, unstakedAmountUI, timeRemainingTillUnlock, unstakingNWithdrawFn, stakingItemInfo } =
    useRestaking()
  const [loading, setLoading] = useState<boolean>(false)
  const rollbar = useRollbar()

  // withdraw modal states
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({ ...DEFAULT_TRANSACTION_DETAIL })

  // atoms
  const [popupState] = useAtom(appStakeUnstakePopupState)
  const setStopUnstakingPopupState = useSetAtom(appStopUnstakingPopupState)
  const restakingAccount = useAtomValue(restakingAccountAtom)
  const refreshStakingData = useSetAtom(refreshRestakingAtom)

  // for switching network
  const network = useAtomValue(networkAtom)

  const handleOpenWithdrawModal = () => {
    setTransactionDetail((prev) => ({ ...prev, isActive: true }))
  }

  const handleCloseWithdrawModal = () => {
    setTransactionDetail({ ...DEFAULT_TRANSACTION_DETAIL })
  }

  const handleUnstaking = async () => {
    if (unstakedAmountUI > 0) {
      if (timeRemainingTillUnlock) {
        setStopUnstakingPopupState({ isOpen: true })
      } else {
        await executeWithdrawing()
      }
    } else {
      handleOpenStakingModal(UNSTAKING)
    }
  }

  const executeWithdrawing = async () => {
    try {
      setLoading(true)
      const result = await unstakingNWithdrawFn(unstakedAmountUI, stakingItemInfo?.decimals ?? DEFAULT_DECIMALS)
      if (!result.isSucceeded) {
        handleRestakingError(
          unstakedAmountUI,
          stakingItemInfo?.symbol,
          restakingAccount,
          result,
          undefined,
          setTransactionDetail,
          rollbar,
          address,
          WITHDRAW_UNSTAKING,
          trans,
        )
      } else {
        await handleRestakingSuccessful(
          network,
          unstakedAmountUI,
          stakingItemInfo?.symbol,
          result,
          setTransactionDetail,
          address,
          WITHDRAW_UNSTAKING,
          trans,
          refreshStakingData,
        )
      }
      handleOpenWithdrawModal()
    } catch (e) {
      console.error(e)
      toast.error(trans.staking.errors.withdraw_staking_token)
      rollbar.error(
        `RELEND: Error when withdrawing unstaking token. Error: Wallet address: ${address}. ${stringifyVariable(e)}`,
      )
    } finally {
      setLoading(false)
    }
  }

  // display
  const totalUnstakedAmountDisplay = useMemo(() => {
    if (!unstakedAmountUI) return null

    return formatToken(unstakedAmountUI, undefined, false, false, undefined, false, {
      display: "inline-block",
    })
  }, [unstakedAmountUI])

  const totalStakedDisplay = useMemo(() => {
    if (!restakingAccount) return "0"

    return formatToken(stakedAmountUI)
  }, [restakingAccount, stakedAmountUI])
  // const totalStakedValueDisplay = restakingAccount ? formatUsd(stakedAmountUI * stakedTokenPrice) : SKELETON_VALUE
  // const totalClaimableValueDisplay = totalClaimable ? formatUsd(totalClaimable * rewardTokenPrice) : SKELETON_VALUE
  const buttonUnstakeDisplay = useMemo(
    () => getButtonTextUnstakeDisplay(restakingAccount, timeRemainingTillUnlock, trans),
    [trans, restakingAccount, timeRemainingTillUnlock],
  )

  // to prevent flashing tooltip content
  const tooltipTimeRemainingContent = useMemo(
    () => (
      <Box>
        <Typography>{trans.staking.detail.cooldown_tooltip_1}</Typography>
        <List className='my-0 py-0 list-disc'>
          <ListItem sx={listItem}>{trans.staking.detail.cooldown_tooltip_2}</ListItem>
          <ListItem sx={listItem}>{trans.staking.detail.cooldown_tooltip_3}</ListItem>
        </List>
        <Typography>{trans.staking.detail.cooldown_tooltip_4}</Typography>
      </Box>
    ),
    [trans],
  )

  const isUnstaking = !!timeRemainingTillUnlock || !!totalUnstakedAmountDisplay

  return (
    <>
      <Stack gap={1} data-testid='staking-detail-unstake'>
        <Stack direction='row' justifyContent='space-between' alignItems='start' gap={1}>
          <ItemInfo
            title={trans.staking.detail.total_staked_rel}
            value={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "start", sm: "center" }}
                gap={{ xs: 0.5, sm: 1 }}
                component='span'
              >
                <span>{totalStakedDisplay}</span>
                {totalUnstakedAmountDisplay && (
                  <Typography component='span' className='text-neutral-600 text-sm'>
                    ({totalUnstakedAmountDisplay} {trans.staking.detail.unstaking})
                  </Typography>
                )}
              </Stack>
            }
          />

          {!isUnstaking && popupState.isOpen && popupState.type !== UNSTAKING && (
            <Link
              className='w-32 text-right text-primary hover:text-primary/80 cursor-pointer'
              onClick={() => handleOpenStakingModal("unstaking")}
              data-testid='staking-detail-unstake-button'
            >
              {trans.staking.modal.unstake}
            </Link>
          )}

          {isUnstaking && (
            <Button
              variant='primary'
              size='md'
              className='w-32'
              onClick={handleUnstaking}
              disabled={!stakedAmountUI && !unstakedAmountUI}
              loading={loading}
              data-testid='restaking-unstake-button'
            >
              {buttonUnstakeDisplay}
            </Button>
          )}
        </Stack>

        {isUnstaking && (
          <>
            {timeRemainingTillUnlock && (
              <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                <Stack direction='row' alignItems='center' gap={1}>
                  <Typography className='text-sm text-neutral-600'>{trans.staking.detail.cooldown_period}</Typography>
                  <RelendTooltip title={tooltipTimeRemainingContent} arrow>
                    <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                  </RelendTooltip>
                </Stack>
                <Typography className='text-orange-600 font-medium text-sm'>{timeRemainingTillUnlock}</Typography>
              </Stack>
            )}
          </>
        )}

        {popupState.isOpen && popupState.type === UNSTAKING && <StakeUnstakeForm isShowForm={!isUnstaking} />}
      </Stack>

      {transactionDetail.isActive && (
        <RelendModal
          open={transactionDetail.isActive}
          title={trans.staking.modal.confirm}
          handleClose={handleCloseWithdrawModal}
          data-testid='modal-staking-detail-withdraw'
          useDrawerOnMobile
          showCloseButton
          isSmallPopup
          hideButtonClose={transactionDetail.isActive}
        >
          <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseWithdrawModal} />
        </RelendModal>
      )}
    </>
  )
}

export default Unstake
